import { useCallback, useMemo, useReducer } from "react";
import type { ShortlistReportContext } from "../types";

export interface SectionVisibility {
	[sectionKey: string]: boolean;
}

export interface FieldVisibility {
	[sectionKey: string]: {
		[fieldKey: string]: boolean;
	};
}

export interface CustomisationState {
	/** Additional rich text content page */
	additionalText: string;
	/** Cover page: created by role text */
	coverCreatedByRole: string;
	/** Custom cover page file (per-report override) */
	coverPageFile: File | null;
	/** Uploaded cover page file ID (from server) */
	coverPageFileId: number | null;
	/** Cover page mode override: 'global' uses schema default, 'default'/'none'/'upload' override */
	coverPageMode: "global" | "default" | "none" | "upload";
	/** Cover page preview URL */
	coverPagePreviewUrl: string | null;
	/** Cover page: presented to text */
	coverPresentedTo: string;
	/** Cover page: presented to role text */
	coverPresentedToRole: string;
	/** Cover page: project name text */
	coverProjectName: string;
	/** Cover page: show logo override (null = use global default) */
	coverShowLogo: boolean | null;
	/** Cover page: supplier role text */
	coverSupplierRole: string;
	/** Field-level toggles within each section */
	fields: FieldVisibility;
	/** Whether to include the additional text page */
	includeAdditionalText: boolean;
	/** Whether to include the project details page */
	includeProjectPage: boolean;
	/** Active preset ID (if loaded) */
	presetId: number | null;
	/** Active preset name */
	presetName: string;
	/** Edited project description (report-specific, not saved to project) */
	projectDescription: string;
	/** Selected project ID for allocation */
	projectId: number | null;
	/** Project page field visibility (which fields to show on project details page) */
	projectVisibleFields: Record<string, boolean>;
	/** Section-level toggles */
	sections: SectionVisibility;
	/** Selected sector IDs for filtering */
	sectorIds: number[];
}

type CustomisationAction =
	| { sectionKey: string; type: "TOGGLE_SECTION"; }
	| { fieldKey: string; sectionKey: string; type: "TOGGLE_FIELD"; }
	| { sections: SectionVisibility; type: "SET_SECTIONS"; }
	| { fields: FieldVisibility; type: "SET_FIELDS"; }
	| { projectId: number | null; type: "SET_PROJECT"; }
	| { description: string; type: "SET_PROJECT_DESCRIPTION"; }
	| { include: boolean; type: "SET_INCLUDE_PROJECT_PAGE"; }
	| { text: string; type: "SET_ADDITIONAL_TEXT"; }
	| { include: boolean; type: "SET_INCLUDE_ADDITIONAL_TEXT"; }
	| { mode: "global" | "default" | "none" | "upload"; type: "SET_COVER_PAGE_MODE"; }
	| { file: File | null; previewUrl: string | null; type: "SET_COVER_PAGE"; }
	| { fileId: number | null; type: "SET_COVER_PAGE_FILE_ID"; }
	| { fields: Record<string, boolean>; type: "SET_PROJECT_VISIBLE_FIELDS"; }
	| { sectorIds: number[]; type: "SET_SECTOR_IDS"; }
	| { type: "SET_COVER_PROJECT_NAME"; value: string }
	| { type: "SET_COVER_SUPPLIER_ROLE"; value: string }
	| { type: "SET_COVER_PRESENTED_TO"; value: string }
	| { type: "SET_COVER_PRESENTED_TO_ROLE"; value: string }
	| { type: "SET_COVER_SHOW_LOGO"; value: boolean | null }
	| { type: "SET_COVER_CREATED_BY_ROLE"; value: string }
	| { preset: Partial<CustomisationState> & { presetId: number; presetName: string }; type: "LOAD_PRESET"; }
	| { type: "RESET_TO_DEFAULTS" }
	| { type: "RESET" };

const createInitialState = (context: ShortlistReportContext): CustomisationState => ({
	sections: context.defaultSections ?? {},
	fields: context.defaultFields ?? {},
	projectId: null,
	projectDescription: "",
	includeProjectPage: false,
	additionalText: "",
	includeAdditionalText: false,
	coverPageMode: "global",
	coverPageFile: null,
	coverPagePreviewUrl: null,
	coverPageFileId: null,
	projectVisibleFields: {
		client_logo: true,
		banner_image: true,
		name: true,
		project_type: true,
		sectors: true,
		location: true,
		project_lead: true,
		description: true,
	},
	sectorIds: context.searchSectorIds ?? [],
	coverProjectName: "",
	coverSupplierRole: "",
	coverPresentedTo: "",
	coverPresentedToRole: "",
	coverShowLogo: null,
	coverCreatedByRole: "",
	presetId: null,
	presetName: "",
});

const reducer = (state: CustomisationState, action: CustomisationAction): CustomisationState => {
	switch (action.type) {
		case "TOGGLE_SECTION": {
			const currentlyVisible = state.sections[action.sectionKey] !== false;
			// Prevent toggling off the last visible section
			const visibleCount = Object.values(state.sections).filter((v) => v !== false).length;
			if (currentlyVisible && visibleCount <= 1) {
				return state;
			}
			const newSections = {
				...state.sections,
				[action.sectionKey]: !currentlyVisible,
			};
			// When toggling a section back on, set all child fields to true
			let newFields = state.fields;
			if (!currentlyVisible && state.fields[action.sectionKey]) {
				const allOn: Record<string, boolean> = {};
				for (const fieldKey of Object.keys(state.fields[action.sectionKey])) {
					allOn[fieldKey] = true;
				}
				newFields = { ...state.fields, [action.sectionKey]: allOn };
			}
			// When toggling a section off, set all child fields to false
			if (currentlyVisible && state.fields[action.sectionKey]) {
				const allOff: Record<string, boolean> = {};
				for (const fieldKey of Object.keys(state.fields[action.sectionKey])) {
					allOff[fieldKey] = false;
				}
				newFields = { ...state.fields, [action.sectionKey]: allOff };
			}
			return { ...state, sections: newSections, fields: newFields };
		}
		case "TOGGLE_FIELD":
			return {
				...state,
				fields: {
					...state.fields,
					[action.sectionKey]: {
						...state.fields[action.sectionKey],
						[action.fieldKey]:
							state.fields[action.sectionKey]?.[action.fieldKey] === false
								? true
								: false,
					},
				},
			};
		case "SET_SECTIONS":
			return { ...state, sections: action.sections };
		case "SET_FIELDS":
			return { ...state, fields: action.fields };
		case "SET_PROJECT":
			return { ...state, projectId: action.projectId };
		case "SET_PROJECT_DESCRIPTION":
			return { ...state, projectDescription: action.description };
		case "SET_INCLUDE_PROJECT_PAGE":
			return { ...state, includeProjectPage: action.include };
		case "SET_ADDITIONAL_TEXT":
			return { ...state, additionalText: action.text };
		case "SET_INCLUDE_ADDITIONAL_TEXT":
			return { ...state, includeAdditionalText: action.include };
		case "SET_COVER_PAGE_MODE":
			return { ...state, coverPageMode: action.mode };
		case "SET_COVER_PAGE":
			return {
				...state,
				coverPageFile: action.file,
				coverPagePreviewUrl: action.previewUrl,
			};
		case "SET_COVER_PAGE_FILE_ID":
			return { ...state, coverPageFileId: action.fileId };
		case "SET_PROJECT_VISIBLE_FIELDS":
			return { ...state, projectVisibleFields: action.fields };
		case "SET_SECTOR_IDS":
			return { ...state, sectorIds: action.sectorIds };
		case "SET_COVER_PROJECT_NAME":
			return { ...state, coverProjectName: action.value };
		case "SET_COVER_SUPPLIER_ROLE":
			return { ...state, coverSupplierRole: action.value };
		case "SET_COVER_PRESENTED_TO":
			return { ...state, coverPresentedTo: action.value };
		case "SET_COVER_PRESENTED_TO_ROLE":
			return { ...state, coverPresentedToRole: action.value };
		case "SET_COVER_SHOW_LOGO":
			return { ...state, coverShowLogo: action.value };
		case "SET_COVER_CREATED_BY_ROLE":
			return { ...state, coverCreatedByRole: action.value };
		case "LOAD_PRESET":
			return {
				...state,
				sections: action.preset.sections ?? state.sections,
				fields: action.preset.fields ?? state.fields,
				presetId: action.preset.presetId,
				presetName: action.preset.presetName,
				coverPageFile: action.preset.coverPageFile ?? state.coverPageFile,
				coverPagePreviewUrl: action.preset.coverPagePreviewUrl ?? state.coverPagePreviewUrl,
				additionalText: action.preset.additionalText ?? state.additionalText,
				includeAdditionalText: action.preset.includeAdditionalText ?? state.includeAdditionalText,
				coverProjectName: action.preset.coverProjectName ?? state.coverProjectName,
				coverSupplierRole: action.preset.coverSupplierRole ?? state.coverSupplierRole,
				coverPresentedTo: action.preset.coverPresentedTo ?? state.coverPresentedTo,
				coverPresentedToRole: action.preset.coverPresentedToRole ?? state.coverPresentedToRole,
				coverShowLogo: action.preset.coverShowLogo !== undefined ? action.preset.coverShowLogo : state.coverShowLogo,
				coverCreatedByRole: action.preset.coverCreatedByRole ?? state.coverCreatedByRole,
				coverPageMode: action.preset.coverPageMode ?? state.coverPageMode,
			};
		case "RESET_TO_DEFAULTS": {
			// Reset all sections to visible and all fields to visible
			const resetSections: SectionVisibility = {};
			for (const key of Object.keys(state.sections)) {
				resetSections[key] = true;
			}
			const resetFields: FieldVisibility = {};
			for (const [sectionKey, fieldMap] of Object.entries(state.fields)) {
				resetFields[sectionKey] = {};
				for (const fieldKey of Object.keys(fieldMap)) {
					resetFields[sectionKey][fieldKey] = true;
				}
			}
			return { ...state, sections: resetSections, fields: resetFields, presetId: null, presetName: "" };
		}
		case "RESET":
			return createInitialState({ companyIds: [], searchSectorIds: state.sectorIds, ingress: "search" });
		default:
			return state;
	}
};

export const useShortlistReportState = (context: ShortlistReportContext) => {
	const [state, dispatch] = useReducer(reducer, context, createInitialState);

	const toggleSection = useCallback((sectionKey: string) => {
		dispatch({ type: "TOGGLE_SECTION", sectionKey });
	}, []);

	const toggleField = useCallback((sectionKey: string, fieldKey: string) => {
		dispatch({ type: "TOGGLE_FIELD", sectionKey, fieldKey });
	}, []);

	const setProject = useCallback((projectId: number | null) => {
		dispatch({ type: "SET_PROJECT", projectId });
	}, []);

	const setProjectDescription = useCallback((description: string) => {
		dispatch({ type: "SET_PROJECT_DESCRIPTION", description });
	}, []);

	const setIncludeProjectPage = useCallback((include: boolean) => {
		dispatch({ type: "SET_INCLUDE_PROJECT_PAGE", include });
	}, []);

	const setAdditionalText = useCallback((text: string) => {
		dispatch({ type: "SET_ADDITIONAL_TEXT", text });
	}, []);

	const setIncludeAdditionalText = useCallback((include: boolean) => {
		dispatch({ type: "SET_INCLUDE_ADDITIONAL_TEXT", include });
	}, []);

	const setCoverPageMode = useCallback((mode: "global" | "default" | "none" | "upload") => {
		dispatch({ type: "SET_COVER_PAGE_MODE", mode });
	}, []);

	const setCoverPage = useCallback((file: File | null, previewUrl: string | null) => {
		dispatch({ type: "SET_COVER_PAGE", file, previewUrl });
	}, []);

	const setCoverPageFileId = useCallback((fileId: number | null) => {
		dispatch({ type: "SET_COVER_PAGE_FILE_ID", fileId });
	}, []);

	const setSections = useCallback((sections: SectionVisibility) => {
		dispatch({ type: "SET_SECTIONS", sections });
	}, []);

	const setFields = useCallback((fields: FieldVisibility) => {
		dispatch({ type: "SET_FIELDS", fields });
	}, []);

	const setProjectVisibleFields = useCallback((fields: Record<string, boolean>) => {
		dispatch({ type: "SET_PROJECT_VISIBLE_FIELDS", fields });
	}, []);

	const setSectorIds = useCallback((sectorIds: number[]) => {
		dispatch({ type: "SET_SECTOR_IDS", sectorIds });
	}, []);

	const setCoverProjectName = useCallback((value: string) => {
		dispatch({ type: "SET_COVER_PROJECT_NAME", value });
	}, []);

	const setCoverSupplierRole = useCallback((value: string) => {
		dispatch({ type: "SET_COVER_SUPPLIER_ROLE", value });
	}, []);

	const setCoverPresentedTo = useCallback((value: string) => {
		dispatch({ type: "SET_COVER_PRESENTED_TO", value });
	}, []);

	const setCoverPresentedToRole = useCallback((value: string) => {
		dispatch({ type: "SET_COVER_PRESENTED_TO_ROLE", value });
	}, []);

	const setCoverShowLogo = useCallback((value: boolean | null) => {
		dispatch({ type: "SET_COVER_SHOW_LOGO", value });
	}, []);

	const setCoverCreatedByRole = useCallback((value: string) => {
		dispatch({ type: "SET_COVER_CREATED_BY_ROLE", value });
	}, []);

	const loadPreset = useCallback(
		(preset: Partial<CustomisationState> & { presetId: number; presetName: string }) => {
			dispatch({ type: "LOAD_PRESET", preset });
		},
		[]
	);

	const resetToDefaults = useCallback(() => {
		dispatch({ type: "RESET_TO_DEFAULTS" });
	}, []);

	const visibleSectionCount = useMemo(
		() => Object.values(state.sections).filter((v) => v !== false).length,
		[state.sections]
	);

	return {
		state,
		dispatch,
		toggleSection,
		toggleField,
		setProject,
		setProjectDescription,
		setIncludeProjectPage,
		setAdditionalText,
		setIncludeAdditionalText,
		setCoverPageMode,
		setCoverPage,
		setCoverPageFileId,
		setProjectVisibleFields,
		setSections,
		setFields,
		setSectorIds,
		setCoverProjectName,
		setCoverSupplierRole,
		setCoverPresentedTo,
		setCoverPresentedToRole,
		setCoverShowLogo,
		setCoverCreatedByRole,
		loadPreset,
		resetToDefaults,
		visibleSectionCount,
	};
};
